import React, { useState } from 'react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { icons } from './ui/Icons';

/**
 * Modal para cadastrar múltiplos horários de uma vez
 * Permite selecionar:
 * - Data inicial e final
 * - Dias da semana
 * - Horários do dia
 */
const BulkSlotModal = ({ isOpen, onClose, onSubmit }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [customTime, setCustomTime] = useState('');
  
  // Novos estados para configuração de horários
  const [timeInterval, setTimeInterval] = useState('30'); // '30' ou '60' minutos
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('18:00');

  // Dias da semana
  const weekDays = [
    { id: 0, label: 'Dom', fullName: 'Domingo' },
    { id: 1, label: 'Seg', fullName: 'Segunda' },
    { id: 2, label: 'Ter', fullName: 'Terça' },
    { id: 3, label: 'Qua', fullName: 'Quarta' },
    { id: 4, label: 'Qui', fullName: 'Quinta' },
    { id: 5, label: 'Sex', fullName: 'Sexta' },
    { id: 6, label: 'Sáb', fullName: 'Sábado' },
  ];

  // Gerar horários baseado no intervalo e horário inicial/final
  const generateTimeSlots = () => {
    const slots = [];
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    
    let currentMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;
    const intervalMinutes = parseInt(timeInterval);
    
    while (currentMinutes < endMinutes) {
      const hours = Math.floor(currentMinutes / 60);
      const minutes = currentMinutes % 60;
      const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      slots.push(timeStr);
      currentMinutes += intervalMinutes;
    }
    
    return slots;
  };

  const toggleDay = (dayId) => {
    if (selectedDays.includes(dayId)) {
      setSelectedDays(selectedDays.filter(d => d !== dayId));
    } else {
      setSelectedDays([...selectedDays, dayId]);
    }
  };

  const toggleTime = (time) => {
    if (selectedTimes.includes(time)) {
      setSelectedTimes(selectedTimes.filter(t => t !== time));
    } else {
      setSelectedTimes([...selectedTimes, time]);
    }
  };

  const addCustomTime = () => {
    if (customTime && !selectedTimes.includes(customTime)) {
      setSelectedTimes([...selectedTimes, customTime].sort());
      setCustomTime('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!startDate || !endDate) {
      alert('Por favor, selecione as datas inicial e final.');
      return;
    }

    if (selectedDays.length === 0) {
      alert('Por favor, selecione pelo menos um dia da semana.');
      return;
    }

    if (selectedTimes.length === 0) {
      alert('Por favor, selecione pelo menos um horário.');
      return;
    }

    // Gerar todos os slots
    const slots = [];
    const start = new Date(startDate + 'T12:00:00');
    const end = new Date(endDate + 'T12:00:00');

    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      const dayOfWeek = date.getDay();
      
      if (selectedDays.includes(dayOfWeek)) {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        const dateStr = `${y}-${m}-${d}`;
        
        selectedTimes.forEach(time => {
          slots.push({
            date: dateStr,
            time: time
          });
        });
      }
    }

    onSubmit(slots);
    handleClose();
  };

  const handleClose = () => {
    setStartDate('');
    setEndDate('');
    setSelectedDays([]);
    setSelectedTimes([]);
    setCustomTime('');
    onClose();
  };

  const selectAllDays = () => {
    setSelectedDays([0, 1, 2, 3, 4, 5, 6]);
  };

  const selectWeekDays = () => {
    setSelectedDays([1, 2, 3, 4, 5]); // Seg a Sex
  };

  const applyGeneratedSchedule = () => {
    const generatedTimes = generateTimeSlots();
    setSelectedTimes([...generatedTimes]);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Cadastrar Horários em Lote">
      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* Período */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Período</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data Inicial
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data Final
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              />
            </div>
          </div>
        </div>

        {/* Dias da Semana */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold text-gray-900">Dias da Semana</h3>
            <div className="space-x-2">
              <Button
                type="button"
                variant="secondary"
                className="text-xs py-1 px-2"
                onClick={selectWeekDays}
              >
                Seg-Sex
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="text-xs py-1 px-2"
                onClick={selectAllDays}
              >
                Todos
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-2">
            {weekDays.map(day => (
              <button
                key={day.id}
                type="button"
                onClick={() => toggleDay(day.id)}
                className={`p-3 rounded-lg text-sm font-semibold transition-all ${
                  selectedDays.includes(day.id)
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                title={day.fullName}
              >
                {day.label}
              </button>
            ))}
          </div>
        </div>

        {/* Horários */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Horários</h3>
          
          {/* Configuração de Intervalo */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Intervalo entre horários
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setTimeInterval('30')}
                className={`p-4 border-2 rounded-lg text-center transition-all ${
                  timeInterval === '30'
                    ? 'border-indigo-600 bg-indigo-50'
                    : 'border-gray-300 bg-white hover:border-gray-400'
                }`}
              >
                <div className={`text-xl font-bold ${timeInterval === '30' ? 'text-indigo-700' : 'text-gray-700'}`}>
                  30 min
                </div>
                <div className="text-xs text-gray-500 mt-1">Mais horários disponíveis</div>
              </button>
              
              <button
                type="button"
                onClick={() => setTimeInterval('60')}
                className={`p-4 border-2 rounded-lg text-center transition-all ${
                  timeInterval === '60'
                    ? 'border-indigo-600 bg-indigo-50'
                    : 'border-gray-300 bg-white hover:border-gray-400'
                }`}
              >
                <div className={`text-xl font-bold ${timeInterval === '60' ? 'text-indigo-700' : 'text-gray-700'}`}>
                  60 min
                </div>
                <div className="text-xs text-gray-500 mt-1">1 hora de intervalo</div>
              </button>
            </div>
          </div>

          {/* Horário Inicial e Final */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Horário de Início
              </label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Horário de Término
              </label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Botão para Aplicar Configuração */}
          <div className="mb-4">
            <Button
              type="button"
              variant="primary"
              className="w-full"
              onClick={applyGeneratedSchedule}
            >
              Gerar Horários ({generateTimeSlots().length} horários)
            </Button>
          </div>

          {/* Preview dos Horários Gerados */}
          {generateTimeSlots().length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Horários que serão criados:
              </p>
              <div className="grid grid-cols-5 gap-2 max-h-40 overflow-y-auto p-2 bg-gray-50 rounded-lg border border-gray-200">
                {generateTimeSlots().map(time => (
                  <button
                    key={time}
                    type="button"
                    onClick={() => toggleTime(time)}
                    className={`p-2 rounded-lg text-sm font-medium transition-all ${
                      selectedTimes.includes(time)
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {time}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Horário Personalizado */}
          <div className="border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Adicionar Horário Personalizado
            </label>
            <div className="flex gap-2">
              <input
                type="time"
                value={customTime}
                onChange={(e) => setCustomTime(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <Button
                type="button"
                variant="secondary"
                onClick={addCustomTime}
                disabled={!customTime}
              >
                {icons.plus({ className: 'h-5 w-5' })}
              </Button>
            </div>
          </div>

          {/* Horários Selecionados */}
          {selectedTimes.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm font-semibold text-blue-900 mb-2">
                Horários selecionados: {selectedTimes.length}
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedTimes.map(time => (
                  <span
                    key={time}
                    className="inline-flex items-center px-2 py-1 bg-white border border-blue-200 rounded text-xs font-medium text-gray-700"
                  >
                    {time}
                    <button
                      type="button"
                      onClick={() => toggleTime(time)}
                      className="ml-1 text-red-500 hover:text-red-700"
                    >
                      {icons.xCircle({ className: 'h-3 w-3' })}
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Resumo */}
        {startDate && endDate && selectedDays.length > 0 && selectedTimes.length > 0 && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm font-semibold text-green-900 mb-1">
              📊 Resumo
            </p>
            <p className="text-sm text-gray-700">
              Serão criados aproximadamente{' '}
              <span className="font-bold">
                {(() => {
                  const start = new Date(startDate + 'T12:00:00');
                  const end = new Date(endDate + 'T12:00:00');
                  let count = 0;
                  for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
                    if (selectedDays.includes(date.getDay())) {
                      count++;
                    }
                  }
                  return count * selectedTimes.length;
                })()}
              </span>{' '}
              horários vagos.
            </p>
          </div>
        )}

        {/* Botões */}
        <div className="flex justify-end space-x-3">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary">
            {icons.check({ className: 'h-5 w-5 mr-2' })}
            Criar Horários
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default BulkSlotModal;
